import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import satelliteApi from '../services/satelliteApi';
import './StreetViewPanel.css';

// Browser-only Maps JS API key. MUST be restricted in Google Cloud Console to
// HTTP referrers matching only your production domains (e.g. *.geoventory.ai).
// Even bundled into JS it is then useless on other origins.
// If unset, the interactive panorama is disabled and we fall back to the
// backend-proxied static image (which carries no key in the URL).
const MAPS_BROWSER_KEY = process.env.REACT_APP_GOOGLE_MAPS_BROWSER_KEY || '';

// Backend proxy base — never includes a key.
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const StreetViewPanel = ({ lat: propLat, lng: propLng, onClose, compact = false }) => {
  const { selectedEvent } = useApp();
  const streetViewRef = useRef(null);
  const panoramaRef = useRef(null);
  const initAttempted = useRef(false);
  
  const [viewMode, setViewMode] = useState('loading'); // 'loading', 'static' or 'interactive'
  const [isLoaded, setIsLoaded] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [heading, setHeading] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [fov, setFov] = useState(90);
  const [streetViewAvailable, setStreetViewAvailable] = useState(true);
  const [locationInfo, setLocationInfo] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [staticImageError, setStaticImageError] = useState(false);
  
  // Location search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [customLocation, setCustomLocation] = useState(null);
  
  // Use custom location, props, selected event, or default
  const lat = customLocation?.lat || propLat || selectedEvent?.location?.coordinates?.[1] || -26.2041;
  const lng = customLocation?.lng || propLng || selectedEvent?.location?.coordinates?.[0] || 28.0473;

  // Debounce function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Search locations
  const searchLocations = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }
      
      setSearching(true);
      try {
        console.log('StreetView searching for:', query);
        const result = await satelliteApi.searchLocations(query);
        console.log('StreetView search result:', result);
        if (result.success && result.predictions?.length > 0) {
          setSearchResults(result.predictions);
          setShowDropdown(true);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } catch (err) {
        console.error('StreetView search error:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300),
    []
  );

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    searchLocations(value);
  };

  // Select location from search
  const selectLocation = async (prediction) => {
    setShowDropdown(false);
    setSearchQuery(prediction.description || prediction.mainText);
    
    try {
      const result = await satelliteApi.getPlaceDetails(prediction.placeId);
      if (result.success && result.location) {
        setCustomLocation({
          lat: result.location.lat,
          lng: result.location.lng,
          name: result.location.name || prediction.description
        });
      }
    } catch (err) {
      console.error('Error getting place details:', err);
    }
  };

  // Get static Street View image URL — proxied through backend so no key is exposed.
  const getStaticStreetViewUrl = useCallback((h = heading, p = pitch, f = fov) => {
    const qs = new URLSearchParams({ lat, lng, heading: h, pitch: p, fov: f, width: 800, height: 600 });
    return `${API_BASE}/maps/streetview?${qs.toString()}`;
  }, [lat, lng, heading, pitch, fov]);

  // Get static map URL for satellite view — proxied through backend.
  const getSatelliteMapUrl = useCallback((zoomLevel = 18) => {
    const qs = new URLSearchParams({ lat, lng, zoom: zoomLevel, width: 800, height: 600, maptype: 'satellite' });
    return `${API_BASE}/maps/static?${qs.toString()}`;
  }, [lat, lng]);

  // Check if Street View is available — proxied through backend.
  const checkStreetViewAvailability = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const qs = new URLSearchParams({ lat, lng });
      const response = await fetch(`${API_BASE}/maps/streetview/metadata?${qs.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json();

      if (data.success && data.available) {
        setStreetViewAvailable(true);
        setLocationInfo({
          description: customLocation?.name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
          date: data.date,
        });
        return true;
      } else {
        setStreetViewAvailable(false);
        return false;
      }
    } catch (error) {
      console.error('Error checking Street View availability:', error);
      return true;
    }
  }, [lat, lng, customLocation]);

  // Re-initialize when location changes
  useEffect(() => {
    // Reset view for new location
    setViewMode('loading');
    setImageLoading(true);
    setStreetViewAvailable(true);
    setStaticImageError(false);
    
    // Clear panorama reference
    if (panoramaRef.current) {
      panoramaRef.current = null;
    }
    
    // Check availability for new location
    checkStreetViewAvailability();
    
    // Reinitialize if API is already loaded
    if (isLoaded && window.google?.maps?.StreetViewPanorama) {
      setViewMode('interactive');
    }
  }, [lat, lng, checkStreetViewAvailability, isLoaded]);

  // Load Google Maps API
  useEffect(() => {
    if (initAttempted.current) return;
    initAttempted.current = true;

    const loadGoogleMapsAPI = () => {
      // If no browser-only key is configured, skip the interactive panorama
      // and stick with the safer static-image-via-backend-proxy view.
      if (!MAPS_BROWSER_KEY) {
        setViewMode('static');
        return;
      }

      // Check if already loaded and working
      if (window.google && window.google.maps && window.google.maps.StreetViewPanorama) {
        console.log('Google Maps API already loaded');
        setIsLoaded(true);
        setViewMode('interactive');
        return;
      }

      // Remove any existing broken script
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existingScript && !window.google?.maps?.StreetViewPanorama) {
        existingScript.remove();
      }

      // Create and load new script — uses the referrer-restricted browser key.
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_BROWSER_KEY}&libraries=geometry&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      // Global callback for when API loads
      window.initGoogleMaps = () => {
        console.log('Google Maps API loaded via callback');
        if (window.google && window.google.maps) {
          setIsLoaded(true);
          setViewMode('interactive');
        }
      };

      script.onerror = (e) => {
        console.error('Failed to load Google Maps API:', e);
        setApiError('Failed to load Maps API');
        setViewMode('static');
      };

      document.head.appendChild(script);

      // Fallback check after timeout
      setTimeout(() => {
        if (window.google && window.google.maps && window.google.maps.StreetViewPanorama) {
          setIsLoaded(true);
          setViewMode('interactive');
        } else {
          // Check current state via ref or just set static as fallback
          setViewMode(prev => prev === 'loading' ? 'static' : prev);
        }
      }, 3000);
    };

    checkStreetViewAvailability();
    loadGoogleMapsAPI();

    return () => {
      delete window.initGoogleMaps;
    };
  }, [checkStreetViewAvailability]);

  // Initialize interactive Street View when API is loaded
  useEffect(() => {
    if (viewMode !== 'interactive' || !isLoaded || !streetViewRef.current) return;
    if (!window.google || !window.google.maps) return;

    const initStreetView = () => {
      try {
        const streetViewService = new window.google.maps.StreetViewService();
        const position = new window.google.maps.LatLng(lat, lng);

        streetViewService.getPanorama(
          { location: position, radius: 100, source: window.google.maps.StreetViewSource.OUTDOOR },
          (data, status) => {
            if (status === 'OK') {
              setStreetViewAvailable(true);
              setLocationInfo({
                description: data.location?.description || 'Street View',
                shortDescription: data.location?.shortDescription || '',
                pano: data.location?.pano
              });

              panoramaRef.current = new window.google.maps.StreetViewPanorama(
                streetViewRef.current,
                {
                  position: data.location.latLng,
                  pov: { heading: heading, pitch: pitch },
                  zoom: zoom,
                  addressControl: true,
                  addressControlOptions: {
                    position: window.google.maps.ControlPosition.BOTTOM_CENTER
                  },
                  linksControl: true,
                  panControl: true,
                  enableCloseButton: false,
                  fullscreenControl: true,
                  motionTracking: false,
                  motionTrackingControl: false,
                  showRoadLabels: true,
                  zoomControl: true
                }
              );

              panoramaRef.current.addListener('pov_changed', () => {
                const pov = panoramaRef.current.getPov();
                setHeading(Math.round(pov.heading));
                setPitch(Math.round(pov.pitch));
              });

              panoramaRef.current.addListener('zoom_changed', () => {
                setZoom(panoramaRef.current.getZoom());
              });

            } else {
              setStreetViewAvailable(false);
            }
          }
        );
      } catch (err) {
        console.error('Street View init error:', err);
        setApiError(err.message);
        setViewMode('static');
      }
    };

    initStreetView();

    return () => {
      if (panoramaRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(panoramaRef.current);
      }
    };
  }, [viewMode, isLoaded, lat, lng]);

  // Update POV for interactive mode
  const updatePOV = (newHeading, newPitch) => {
    if (viewMode === 'interactive' && panoramaRef.current) {
      panoramaRef.current.setPov({ heading: newHeading, pitch: newPitch });
    }
    setHeading(newHeading);
    setPitch(newPitch);
  };

  // Rotate view
  const rotate = (direction) => {
    const newHeading = (heading + direction + 360) % 360;
    updatePOV(newHeading, pitch);
  };

  // Look up/down
  const tilt = (direction) => {
    const newPitch = Math.max(-90, Math.min(90, pitch + direction));
    updatePOV(heading, newPitch);
  };

  // Zoom in/out (for static mode, adjust FOV)
  const adjustZoom = (delta) => {
    if (viewMode === 'static') {
      const newFov = Math.max(30, Math.min(120, fov - delta * 15));
      setFov(newFov);
    } else if (panoramaRef.current) {
      const newZoom = Math.max(0, Math.min(5, zoom + delta));
      panoramaRef.current.setZoom(newZoom);
      setZoom(newZoom);
    }
  };

  // Reset view
  const resetView = () => {
    updatePOV(0, 0);
    setFov(90);
    if (panoramaRef.current) {
      panoramaRef.current.setZoom(1);
    }
    setZoom(1);
  };

  // Get compass direction
  const getCompassDirection = (deg) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(((deg % 360) + 360) % 360 / 45) % 8;
    return directions[index];
  };

  // Handle static image load
  const handleImageLoad = () => {
    setImageLoading(false);
    setStaticImageError(false);
  };

  // Handle static image error
  const handleImageError = () => {
    setImageLoading(false);
    setStaticImageError(true);
  };

  // Render static Street View mode (fallback)
  const renderStaticMode = () => (
    <div className="sv-static-container">
      {imageLoading && (
        <div className="sv-loading">
          <div className="sv-loading-spinner"></div>
          <p>Loading Street View...</p>
        </div>
      )}
      
      {streetViewAvailable && !staticImageError ? (
        <div className="sv-static-wrapper">
          <img 
            src={getStaticStreetViewUrl()}
            alt={`Street View at ${lat.toFixed(4)}, ${lng.toFixed(4)}`}
            className="sv-static-image"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: imageLoading ? 'none' : 'block' }}
          />
          <div className="sv-static-overlay">
            <span className="sv-static-badge">📷 Static View</span>
          </div>
        </div>
      ) : (
        <div className="sv-fallback-container">
          <div className="sv-unavailable-notice">
            <span className="sv-unavailable-icon">🛰️</span>
            <p>Street View not available at this location</p>
            <p className="sv-coords-small">Showing satellite view instead</p>
          </div>
          <img 
            src={getSatelliteMapUrl()}
            alt={`Satellite view at ${lat.toFixed(4)}, ${lng.toFixed(4)}`}
            className="sv-satellite-image"
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        </div>
      )}
    </div>
  );

  // Render interactive Street View mode
  const renderInteractiveMode = () => (
    <div className="sv-container">
      {!isLoaded && (
        <div className="sv-loading">
          <div className="sv-loading-spinner"></div>
          <p>Loading Street View...</p>
        </div>
      )}
      <div 
        ref={streetViewRef} 
        className="sv-panorama"
        style={{ display: isLoaded ? 'block' : 'none' }}
      />
    </div>
  );

  return (
    <div className={`street-view-panel ${compact ? 'compact' : ''}`}>
      {/* Header */}
      <div className="sv-header">
        <div className="sv-title">
          <span className="sv-icon">🏠</span>
          <h3>Street View 360°</h3>
          {viewMode === 'loading' && (
            <span className="sv-badge sv-badge-loading">Loading...</span>
          )}
          {viewMode === 'interactive' && streetViewAvailable && (
            <span className="sv-badge">Interactive</span>
          )}
          {viewMode === 'static' && (
            <span className="sv-badge sv-badge-static">Static</span>
          )}
        </div>
        <div className="sv-header-actions">
          {/* Mode toggle */}
          {streetViewAvailable && (
            <div className="sv-mode-toggle">
              <button 
                className={`sv-mode-btn ${viewMode === 'static' ? 'active' : ''}`}
                onClick={() => setViewMode('static')}
                title="Static Image Mode"
              >
                📷
              </button>
              <button 
                className={`sv-mode-btn ${viewMode === 'interactive' ? 'active' : ''}`}
                onClick={() => {
                  if (isLoaded) {
                    setViewMode('interactive');
                  }
                }}
                title="Interactive 360° Mode"
                disabled={!isLoaded || !!apiError}
              >
                🔄
              </button>
            </div>
          )}
          {onClose && (
            <button className="sv-close-btn" onClick={onClose}>✕</button>
          )}
        </div>
      </div>

      {/* Location Info */}
      <div className="sv-location-info">
        <span className="sv-location-icon">📍</span>
        <span className="sv-location-text">
          {locationInfo?.description || customLocation?.name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`}
        </span>
        {customLocation && (
          <button 
            className="sv-clear-location"
            onClick={() => {
              setCustomLocation(null);
              setSearchQuery('');
              initAttempted.current = false;
              setViewMode('loading');
            }}
            title="Clear custom location"
          >
            ✕
          </button>
        )}
        {locationInfo?.date && (
          <span className="sv-location-date">📅 {locationInfo.date}</span>
        )}
      </div>

      {/* Location Search */}
      <div className="sv-search-container">
        <div className="sv-search-wrapper">
          <input
            type="text"
            placeholder="Search location..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
            className="sv-search-input"
          />
          <span className="sv-search-icon">{searching ? '⟳' : '🔍'}</span>
        </div>
        
        {showDropdown && searchResults.length > 0 && (
          <div className="sv-search-dropdown">
            {searchResults.map((prediction, index) => (
              <div
                key={index}
                className="sv-search-result"
                onClick={() => selectLocation(prediction)}
              >
                <span className="sv-result-icon">📍</span>
                <div className="sv-result-text">
                  <span className="sv-result-main">{prediction.mainText || prediction.description}</span>
                  {prediction.secondaryText && (
                    <span className="sv-result-secondary">{prediction.secondaryText}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* API Error Notice */}
      {apiError && viewMode === 'static' && (
        <div className="sv-api-notice">
          <span>ℹ️</span>
          <span>Using static view mode. Enable Maps JavaScript API in Google Cloud Console for interactive 360° view.</span>
        </div>
      )}

      {/* Main View */}
      {viewMode === 'loading' ? (
        <div className="sv-container">
          <div className="sv-loading">
            <div className="sv-loading-spinner"></div>
            <p>Loading Google Maps API...</p>
          </div>
        </div>
      ) : viewMode === 'static' ? renderStaticMode() : renderInteractiveMode()}

      {/* Controls */}
      <div className="sv-controls">
        {/* Direction Controls */}
        <div className="sv-control-group">
          <span className="control-label">Rotate</span>
          <div className="sv-direction-controls">
            <button className="sv-btn" onClick={() => rotate(-45)} title="Rotate Left">
              ⟲
            </button>
            <button className="sv-btn" onClick={() => rotate(45)} title="Rotate Right">
              ⟳
            </button>
          </div>
        </div>

        {/* Tilt Controls */}
        <div className="sv-control-group">
          <span className="control-label">Tilt</span>
          <div className="sv-tilt-controls">
            <button className="sv-btn" onClick={() => tilt(15)} title="Look Up">
              ▲
            </button>
            <button className="sv-btn" onClick={() => tilt(-15)} title="Look Down">
              ▼
            </button>
          </div>
        </div>

        {/* Zoom Controls */}
        <div className="sv-control-group">
          <span className="control-label">Zoom</span>
          <div className="sv-zoom-controls">
            <button className="sv-btn" onClick={() => adjustZoom(1)} title="Zoom In">
              ➕
            </button>
            <button className="sv-btn" onClick={() => adjustZoom(-1)} title="Zoom Out">
              ➖
            </button>
          </div>
        </div>

        {/* Compass Display */}
        <div className="sv-compass">
          <div className="compass-ring">
            <div 
              className="compass-needle" 
              style={{ transform: `rotate(${heading}deg)` }}
            />
          </div>
          <span className="compass-direction">
            {getCompassDirection(heading)} ({heading}°)
          </span>
        </div>

        {/* Reset */}
        <button className="sv-btn sv-reset-btn" onClick={resetView}>
          🔄 Reset
        </button>
      </div>

      {/* Info Panel */}
      <div className="sv-info-panel">
        <div className="sv-info-item">
          <span className="info-label">Heading</span>
          <span className="info-value">{heading}°</span>
        </div>
        <div className="sv-info-item">
          <span className="info-label">Pitch</span>
          <span className="info-value">{pitch}°</span>
        </div>
        <div className="sv-info-item">
          <span className="info-label">{viewMode === 'static' ? 'FOV' : 'Zoom'}</span>
          <span className="info-value">{viewMode === 'static' ? `${fov}°` : `${zoom}x`}</span>
        </div>
        <div className="sv-info-item">
          <span className="info-label">Location</span>
          <span className="info-value">{lat.toFixed(4)}, {lng.toFixed(4)}</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="sv-quick-actions">
        <a 
          href={getStaticStreetViewUrl()}
          download={`streetview_${lat}_${lng}.jpg`}
          className="sv-action-btn"
        >
          📥 Download Image
        </a>
      </div>

      {/* Tips */}
      <div className="sv-tips">
        <span className="tip-icon">💡</span>
        <span className="tip-text">
          {viewMode === 'static' 
            ? 'Use rotate/tilt/zoom controls to change view angle. Click Download to save image.'
            : 'Click and drag to look around. Use scroll wheel to zoom. Click arrows to move.'}
        </span>
      </div>
    </div>
  );
};

export default StreetViewPanel;
